import { useState, useEffect } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Tab,
  Tabs,
  Paper,
  Box,
  Card,
  Typography,
  TextField,
  Pagination,
  Button,
} from "@mui/material";
import PdfIcon from "@mui/icons-material/PictureAsPdf";
import { CalendarToday } from "@mui/icons-material";
import api from "../../../services/api/api";
import Swal from "sweetalert2";

const UltimasPublicacoes = () => {
  const theme = useTheme();
  const [tabIndex, setTabIndex] = useState(0);
  const [categorias, setCategorias] = useState([]);
  const [dados, setDados] = useState([]);
  const [dadosFiltrados, setDadosFiltrados] = useState([]);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [pesquisa, setPesquisa] = useState("");

  const ITENS_POR_PAGINA = 5;

  const handleTabChange = (event, newIndex) => {
    setTabIndex(newIndex);
    setPaginaAtual(1);
    setPesquisa("");
    filtrarDados(categorias[newIndex]);
  };

  const handlePesquisa = (event) => {
    const texto = event.target.value.toLowerCase();
    setPesquisa(texto);
    setPaginaAtual(1);
    let filtrados = dados.filter(
      (item) => item.category === categorias[tabIndex]
    );
    filtrados = filtrados.filter(
      (item) =>
        item.name.toLowerCase().includes(texto) ||
        item.file.toLowerCase().includes(texto)
    );
    // Ordena por data decrescente
    filtrados.sort((a, b) => new Date(b.publicationDate) - new Date(a.publicationDate));
    setDadosFiltrados(filtrados);
  };

  const handlePaginaChange = (event, novaPagina) => {
    setPaginaAtual(novaPagina);
  };

  const filtrarDados = (categoria) => {
    let filtrados = dados.filter((item) => item.category === categoria);
    // Ordena por data decrescente
    filtrados.sort((a, b) => new Date(b.publicationDate) - new Date(a.publicationDate));
    setDadosFiltrados(filtrados);
  };

  const carregarDados = async () => {
    try {
      const response = await api.get("api/getAllPublications");
      const dadosAPI = response.data;

      const publicacoesPublicadas = dadosAPI
        .filter((item) => item.isPublished)
        // Ordena por data decrescente
        .sort((a, b) => new Date(b.publicationDate) - new Date(a.publicationDate));

      const categoriasUnicas = [
        ...new Set(publicacoesPublicadas.map((item) => item.category)),
      ];
      setCategorias(categoriasUnicas);
      setDados(publicacoesPublicadas);

      const indiceAta = categoriasUnicas.indexOf("Ata");
      if (indiceAta !== -1) {
        setTabIndex(indiceAta);
        filtrarDados(categoriasUnicas[indiceAta]);
      } else if (categoriasUnicas.length > 0) {
        setTabIndex(0);
        filtrarDados(categoriasUnicas[0]);
      }
    } catch (error) {
      console.error("Erro ao carregar dados:", error.message);
      
      // Substituição do alert comum por SweetAlert2
      Swal.fire({
        title: 'Erro ao carregar publicações',
        text: 'Verifique a conexão com o servidor e tente novamente.',
        icon: 'error',
        confirmButtonText: 'Ok',
        confirmButtonColor: theme.palette.primary.main,
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        customClass: {
          popup: 'swal-popup',
          title: 'swal-title',
          content: 'swal-content'
        }
      });
    }
  };

  const handleVisualizarPDF = (fileUrl, fileName) => {
    try {
      window.open(fileUrl, "_blank");
    } catch (error) {
      // Caso haja erro ao abrir o PDF
      Swal.fire({
        title: 'Erro ao abrir arquivo',
        text: `Não foi possível abrir o arquivo "${fileName}". Verifique se o link está correto.`,
        icon: 'warning',
        confirmButtonText: 'Ok',
        confirmButtonColor: theme.palette.primary.main,
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        customClass: {
          popup: 'swal-popup',
          title: 'swal-title',
          content: 'swal-content'
        }
      });
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    if (categorias.length > 0) {
      filtrarDados(categorias[tabIndex]);
    }
  }, [tabIndex, categorias]);

  const itensExibidos = dadosFiltrados.slice(
    (paginaAtual - 1) * ITENS_POR_PAGINA,
    paginaAtual * ITENS_POR_PAGINA
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Typography fontSize={25} fontWeight={700}>
        Últimas publicações
      </Typography>
      <Paper
        square
        sx={{ width: "100%", maxWidth: "1200px", overflow: "hidden" }}
      >
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            width: "100%",
            maxWidth: "100%",
            overflowX: "auto",
          }}
        >
          {categorias.map((categoria, index) => (
            <Tab key={index} label={categoria} />
          ))}
        </Tabs>
      </Paper>

      <TextField
        label="Pesquisar"
        variant="outlined"
        size="small"
        sx={{ marginTop: 2, width: "100%" }}
        value={pesquisa}
        onChange={handlePesquisa}
      />
      <Box sx={{ marginTop: 2, width: "100%" }}>
        {itensExibidos.length > 0 ? (
          itensExibidos.map((item) => (
            <Card
              key={item.id}
              sx={{
                marginBottom: 1,
                borderRadius: 2,
                padding: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                height: "auto",
                flexDirection: { xs: "column", md: "row" },
                width: "100%",
              }}
            >
              <Box sx={{width: "95%"}}>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: 0.5,
                    }}
                  >
                    <CalendarToday
                      fontSize="small"
                      sx={{ marginRight: 1, color: "text.secondary" }}
                    />
                    {new Date(item.publicationDate).toLocaleDateString("pt-BR")}
                  </Typography>

                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      marginBottom: 0.5,
                      textTransform: "uppercase",
                    }}
                  >
                    {item.name}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                onClick={() => handleVisualizarPDF(item.file, item.name)}
                sx={{ background: theme.palette.primary.main }}
              >
                Visualizar <PdfIcon sx={{ marginLeft: "5px" }} />
              </Button>
            </Card>
          ))
        ) : (
          <Typography
            variant="body1"
            sx={{
              textAlign: "center",
              color: "text.secondary",
              marginTop: 4,
              fontStyle: "italic",
            }}
          >
            Nenhum resultado encontrado.
          </Typography>
        )}
      </Box>
      {dadosFiltrados.length > ITENS_POR_PAGINA && (
        <Pagination
          count={Math.ceil(dadosFiltrados.length / ITENS_POR_PAGINA)}
          page={paginaAtual}
          onChange={handlePaginaChange}
          sx={{ marginTop: 2 }}
        />
      )}
    </Box>
  );
};

export default UltimasPublicacoes;